import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';
import moment from 'moment';
import find from 'lodash/find';
import padStart from 'lodash/padStart';

import { inflect, shakeElement } from '../../utils/utilsGlobal';
import { searchJiraTasks } from '../../services/apiChat';
import {
  EditableSelect,
  Autocomplete,
  Dropdown,
  Modal,
} from '../../components';

import uiStyles from '../../styles/ui.scss';
import styles from './CreateTaskModal.scss';

const mapStateToProps = state => {
  return {
    currentCompany: state.currentCompany,
    currentRoom: state.currentRoom,
    reminderTypes: state.reminderTypes,
  };
};

@connect(mapStateToProps)
export class CreateTaskModal extends Component {

  taskSelect = React.createRef();
  setDateTime = React.createRef();

  state = {
    taskType: null,
    taskComment: '',
    scheduleOption: 60 * 60,
    scheduledInDays: 2,
    scheduledInHours: 0,
    scheduledInMinutes: 0,
    scheduledOnDate: null,
    scheduledOnMonth: null,
    scheduledOnTimeHours: null,
    scheduledOnTimeMinutes: null,
    jiraSelectedTask: null,
    jiraAutocompleteOptions: [],
  };

  componentDidMount(){
    const { reminderTypes } = this.props;
    const defaultScheduleDate = moment().add(30, 'minutes').toDate();

    this.setState({
      taskType: reminderTypes[0]?.id || null,
      scheduledOnDate: defaultScheduleDate.getDate(),
      scheduledOnMonth: defaultScheduleDate.getMonth(),
      scheduledOnTimeHours: padStart(defaultScheduleDate.getHours(), 2, 0),
      scheduledOnTimeMinutes: padStart(defaultScheduleDate.getMinutes(), 2, 0),
    });
  }

  componentDidUpdate(prevProps){
    const { reminderTypes } = this.props;
    if (reminderTypes !== prevProps.reminderTypes) {
      this.setState({
        taskType: reminderTypes[0]?.id || null
      });
    }
  }

  setSelectedValue = (type, value) => {
    this.setState({ [type]: value });
  };

  generateMonthOptions(){
    const months = [
      'января',
      'февраля',
      'марта',
      'апреля',
      'мая',
      'июня',
      'июля',
      'августа',
      'сентября',
      'октября',
      'ноября',
      'декабря',
    ];
    return months.map((month, index) => {
      return {
        title: month,
        value: index,
      };
    });
  }

  onScheduleButtonClick = () => {
    const now = new Date();
    const { onSubmit } = this.props;
    const {
      taskType,
      taskComment,
      scheduleOption,
      scheduledInDays,
      scheduledInHours,
      scheduledInMinutes,
      scheduledOnDate,
      scheduledOnMonth,
      scheduledOnTimeHours,
      scheduledOnTimeMinutes,
    } = this.state;

    let scheduledOn;
    if (scheduleOption === 'set-timer') {
      const scheduledInSeconds = scheduledInDays * 24 * 60 * 60
        + scheduledInHours * 60 * 60
        + scheduledInMinutes * 60;
      scheduledOn = moment().add(scheduledInSeconds, 'seconds');
    }
    else if (scheduleOption === 'select-date-time') {
      const scheduleDate = new Date(
        now.getFullYear(),
        scheduledOnMonth,
        scheduledOnDate,
        scheduledOnTimeHours,
        scheduledOnTimeMinutes,
      );
      if (scheduleDate < now) {
        if (moment(now).diff(scheduleDate, 'days') > 182 /* half a year */) {
          scheduleDate.setFullYear(now.getFullYear() + 1);
        }
        else {
          shakeElement(this.setDateTime.current);
          return;
        }
      }
      scheduledOn = moment(scheduleDate);
    }
    else {
      scheduledOn = moment().add(scheduleOption, 'seconds');
    }
    onSubmit({
      taskType,
      comment: taskComment.trim(),
      timestamp: scheduledOn.toDate(),
    });
  };

  onLinkToJiraTaskClick = () => {
    const { onSubmit } = this.props;
    const { taskType, jiraSelectedTask } = this.state;
    onSubmit({
      taskType,
      jiraTask: jiraSelectedTask || {},
    });
  };

  onJiraInputType = async (query) => {
    const { currentCompany } = this.props;
    const jiraTaskId = this.extractJiraTaskIdFromUrl(query);
    const jiraTasks = await searchJiraTasks(currentCompany.id, jiraTaskId || query);
    const jiraAutocompleteOptions = jiraTasks.map(item => {
      return {
        title: item.providerId + ': ' + item.title,
        value: item.providerId,
      };
    });
    this.setState({ jiraAutocompleteOptions });
  };

  onJiraInputSelect = (option) => {
    this.setState({
      jiraSelectedTask: {
        id: option.value,
        title: option.title,
      },
    });
  };

  extractJiraTaskIdFromUrl = (jiraUrl) => {
    const jiraTaskId = jiraUrl.trim().replace(/^http.*\/([a-z]{1,8})-([0-9]{1,6})$/i, '$1-$2');
    return jiraTaskId === jiraUrl ? null : jiraTaskId;
  };

  getTaskSelectOptions = () => {
    const { reminderTypes } = this.props;
    const reminderOptions = reminderTypes.map(type => {
      return {
        value: type.id,
        title: type.title,
      }
    });
    return [...reminderOptions, {
      value: 'link-to-jira',
      title: 'Связать с задачей в Jira',
    }];
  };

  onTaskCommentChange = (e) => {
    this.setState({ taskComment: e.target.value });
  };

  render(){
    const { className, onClose } = this.props;
    const {
      taskType,
      taskComment,
      scheduleOption,
      scheduledInDays,
      scheduledInHours,
      scheduledInMinutes,
      scheduledOnDate,
      scheduledOnMonth,
      scheduledOnTimeHours,
      scheduledOnTimeMinutes,
      jiraAutocompleteOptions,
    } = this.state;

    const taskSelectOptions = this.getTaskSelectOptions();
    const taskSelectSelectedOption = find(taskSelectOptions, { value: taskType }) || {};

    return (
      <Modal className={className} onClose={onClose} title="Новая задача" verticallyCentered={true}>

        <Dropdown
          buttonAppearance={true}
          onChange={({ value }) => this.setSelectedValue('taskType', value)}
          items={taskSelectOptions}>
          {taskSelectSelectedOption.title}
        </Dropdown>

        {taskType !== 'link-to-jira' && (
          <Fragment>
            <div className={styles.taskSettings}>
              <EditableSelect className={styles.dateTimeSelect}
                onChange={({ value }) => this.setSelectedValue('scheduleOption', value)}
                defaultValue={scheduleOption}
                options={[
                  {
                    value: 5 * 60,
                    title: 'Через 5 минут',
                  },
                  {
                    value: 10 * 60,
                    title: 'Через 10 минут',
                  },
                  {
                    value: 15 * 60,
                    title: 'Через 15 минут',
                  },
                  {
                    value: 30 * 60,
                    title: 'Через 30 минут',
                  },
                  {
                    value: 60 * 60,
                    title: 'Через час',
                  },
                  {
                    value: 2 * 60 * 60,
                    title: 'Через 2 часа',
                  },
                  {
                    value: 24 * 60 * 60,
                    title: 'Через день',
                  },
                  {
                    value: 'set-timer',
                    title: 'Через...',
                    selectedTitle: 'Через',
                    dividerAbove: true,
                  },
                  {
                    value: 'select-date-time',
                    title: 'Выбрать дату и время',
                    selectedTitle: 'Дата и время',
                    dividerAbove: true,
                  },
                ]}/>

              {(scheduleOption === 'set-timer') && (
                <Fragment>
                  <EditableSelect className={styles.dateTimeSelect}
                    options={[0, 1, 2, 3, 4, 5, 6, 7]}
                    onChange={({ value }) => this.setSelectedValue('scheduledInDays', value)}
                    editable={true}
                    getValueSuffix={(value) => inflect(value, ['день', 'дня', 'дней'], true)}
                    defaultValue={scheduledInDays}
                    type="number"
                    min={0}
                    max={365}/>

                  <EditableSelect className={styles.dateTimeSelect}
                    options={[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]}
                    onChange={({ value }) => this.setSelectedValue('scheduledInHours', value)}
                    editable={true}
                    getValueSuffix={(value) => inflect(value, ['час', 'часа', 'часов'], true)}
                    defaultValue={scheduledInHours}
                    type="number"
                    min={0}
                    flag={1}
                    max={23}/>

                  <EditableSelect className={styles.dateTimeSelect}
                    options={[0, 5, 10, 15, 30, 45]}
                    onChange={({ value }) => this.setSelectedValue('scheduledInMinutes', value)}
                    editable={true}
                    getValueSuffix={(value) => inflect(value, ['минуту', 'минуты', 'минут'], true)}
                    defaultValue={scheduledInMinutes}
                    type="number"
                    min={0}
                    max={59}/>
                </Fragment>
              )}

              {(scheduleOption === 'select-date-time') && (
                <div className={styles.dateTimeBlock} ref={this.setDateTime}>

                  <EditableSelect className={styles.dateTimeSelect}
                    onChange={({ value }) => this.setSelectedValue('scheduledOnDate', value)}
                    editable={true}
                    defaultValue={scheduledOnDate}
                    type="number"
                    min={0}
                    max={31}/>

                  <EditableSelect className={styles.dateTimeSelect}
                    onChange={({ value }) => this.setSelectedValue('scheduledOnMonth', value)}
                    defaultValue={scheduledOnMonth}
                    options={this.generateMonthOptions()}/>

                  <span className={styles.dateTimeSelect}>в</span>
                  <EditableSelect
                    onChange={({ value }) => this.setSelectedValue('scheduledOnTimeHours', value)}
                    editable={true}
                    defaultValue={scheduledOnTimeHours}
                    type="number"
                    min={0}
                    max={23}/>

                  <span className={styles.dateTimeColon}>:</span>
                  <EditableSelect
                    onChange={({ value }) => this.setSelectedValue('scheduledOnTimeMinutes', value)}
                    editable={true}
                    defaultValue={scheduledOnTimeMinutes}
                    type="number"
                    min={0}
                    max={59}/>
                </div>
              )}
            </div>
            <button className={cn(uiStyles.buttonPrimary, styles.submitButton)}
              onClick={this.onScheduleButtonClick}>
              Поставить задачу
            </button>
            <textarea className={styles.taskComment}
              value={taskComment}
              onChange={this.onTaskCommentChange}/>
          </Fragment>
        )}

        {taskType === 'link-to-jira' && (
          <Fragment>
            <div className={styles.taskSettings}>
              <Autocomplete className={styles.jiraAutocomplete}
                items={jiraAutocompleteOptions}
                placeholder="Ссылка, название или номер задачи"
                onType={this.onJiraInputType}
                onSelect={this.onJiraInputSelect}/>
            </div>
            <button className={cn(uiStyles.buttonPrimary, styles.submitButton)}
              onClick={this.onLinkToJiraTaskClick}>
              Связать с задачей в Jira
            </button>
          </Fragment>
        )}

      </Modal>
    );
  }
}
